import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Link } from '../components/nav';
import { loadSearchIndex } from '../data/load';
import { PLACE_TYPE_LABEL, STATUS_LABEL, type SearchIndex } from '../data/types';
import { useAppData } from '../state/AppData';
import { findInParagraphs, findPlaces } from '../features/search/search';
import { chapterRoman } from '../features/reader/segments';

/** Buscar (fase E). Dos ámbitos distintos y declarados: el texto de la colección que se está
 *  leyendo —su índice se descarga solo al buscar— y los lugares de todo el libro, que caben en un
 *  índice mínimo de 88 KB. Si un lugar está en otra parte del volumen, el resultado lo dice y el
 *  enlace cambia de ámbito. */
export function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const { city, cities, placeIndex } = useAppData();
  const [index, setIndex] = useState<{ col?: string; data?: SearchIndex; error?: string }>({});
  const col = city.collectionId;

  useEffect(() => {
    if (!q) return;
    let live = true;
    loadSearchIndex(col)
      .then((data) => { if (live) setIndex({ col, data }); })
      .catch((e: unknown) => { if (live) setIndex({ col, error: e instanceof Error ? e.message : String(e) }); });
    return () => { live = false; };
  }, [col, q]);

  const rows = index.col === col ? index.data : undefined;
  const textHits = useMemo(() => (rows ? findInParagraphs(rows.paragraphs, q) : []), [rows, q]);
  const placeHits = useMemo(() => findPlaces(placeIndex, q), [placeIndex, q]);
  const label = (c: string) => cities.find((x) => x.collectionId === c);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const v = new FormData(e.currentTarget).get('q');
    const next = new URLSearchParams(params);
    if (typeof v === 'string' && v.trim()) next.set('q', v.trim()); else next.delete('q');
    setParams(next, { replace: true });
  };

  return (
    <div className="page search">
      <h1>Buscar</h1>
      <form className="search__form" onSubmit={onSubmit} role="search">
        <label htmlFor="q">Buscar en el texto de {city.section} y en los lugares de todo el libro</label>
        <div className="search__row">
          <input id="q" name="q" type="search" defaultValue={q} key={q} placeholder="por ejemplo: casa de las Conchas" autoComplete="off" />
          <button type="submit" className="btn btn--primary">Buscar</button>
        </div>
        <p className="search__note">Se busca la frase entera, sin distinguir tildes ni mayúsculas. El texto es el de 1884: escribe «ábside bizantino» donde hoy diríamos románico.</p>
      </form>

      {!q && <p className="search__empty">Escribe algo para buscar.</p>}

      {q && (
        <section aria-labelledby="res-lugares">
          <h2 id="res-lugares">Lugares <span className="count">({placeHits.length})</span></h2>
          {placeHits.length === 0 && <p>Ningún lugar del libro se llama así.</p>}
          <ul className="search__places">
            {placeHits.map((p) => {
              const other = p.c !== col;
              const scope = label(p.c);
              const to = `/explorar?place=${p.id}${other ? `&ciudad=${scope?.slug ?? ''}` : ''}`;
              return (
                <li key={p.id}>
                  {other
                    ? <RouterLink to={to}>{p.n}</RouterLink>
                    : <Link to={`/explorar?place=${p.id}`}>{p.n}</Link>}
                  <span className="search__meta">
                    {PLACE_TYPE_LABEL[p.t] ?? p.t} · {STATUS_LABEL[p.s] ?? p.s}
                    {p.m && <> · {p.m}</>}
                    {other && <> · <em>en {scope?.section ?? p.c}</em></>}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {q && (
        <section aria-labelledby="res-texto">
          <h2 id="res-texto">En el texto de {city.section} <span className="count">({textHits.length})</span></h2>
          {index.error && <p role="alert">No se pudo cargar el índice de búsqueda: {index.error}</p>}
          {!rows && !index.error && <p role="status">Preparando el índice de búsqueda…</p>}
          {rows && textHits.length === 0 && <p>Ninguna aparición en estos capítulos. El buscador no cruza de ámbito: cambia de ámbito en la cabecera para buscar en otra parte del libro.</p>}
          <ol className="search__hits">
            {textHits.map((h) => (
              <li key={h.paragraphId}>
                <Link to={`/leer/${h.chapterId}?paragraph=${h.paragraphId}`} className="search__hit">
                  <span className="search__ref">Cap. {chapterRoman(h.paragraphId)}{h.printedPage ? `, p. ${h.printedPage}` : ''}{h.count > 1 ? ` · ${h.count} apariciones` : ''}</span>
                  <span className="search__excerpt">…{h.before}<mark>{h.match}</mark>{h.after}…</span>
                </Link>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
